import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import seoPages from "../seo/pages.json";
import { SITE_URL, absoluteUrl, isEnglishPath } from "../lib/routes";

const DEFAULT_IMAGE = seoPages.image;

function upsertMeta(selector, attributes) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (value != null) el.setAttribute(key, value);
  });
  return el;
}

function upsertLink(rel, attributes) {
  const extra = attributes.hreflang
    ? `[hreflang="${attributes.hreflang}"]`
    : "";
  let el = document.head.querySelector(`link[rel="${rel}"]${extra}`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (value != null) el.setAttribute(key, value);
  });
}

function removeLinks(rel) {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function pageFromPath(pathname) {
  if (pathname === "/fr/home" || pathname === "/fr" || pathname === "/fr/") {
    return seoPages.pages["/"];
  }
  return seoPages.pages[pathname];
}

export default function SEO({ title, description, image, canonical, noindex = false }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = pageFromPath(pathname) || {};
    const language = page.lang || (isEnglishPath(pathname) ? "en" : "fr");
    const shouldNoindex = Boolean(page.noindex) || noindex || !page.title;

    const finalTitle = title || page.title || "HollowCorp";
    const finalDescription =
      description ||
      page.description ||
      "Agence web française — création de sites internet, SEO et maintenance.";
    const canonicalPath = pathname === "/fr/home" ? "/" : pathname;
    const canonicalUrl = canonical || absoluteUrl(shouldNoindex ? pathname : canonicalPath);
    const imageUrl = image || DEFAULT_IMAGE;
    const alternate = page.alternate;

    document.documentElement.lang = language === "en" ? "en" : "fr";
    document.title = finalTitle;

    upsertMeta('meta[name="description"]', { name: "description", content: finalDescription });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: shouldNoindex
        ? "noindex, nofollow, noarchive"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    upsertMeta('meta[name="theme-color"]', { name: "theme-color", content: "#050505" });

    if (shouldNoindex) {
      removeLinks("canonical");
      removeLinks("alternate");
    } else {
      upsertLink("canonical", { href: canonicalUrl });
      if (alternate) {
        const frHref = language === "fr" ? canonicalUrl : absoluteUrl(alternate);
        const enHref = language === "en" ? canonicalUrl : absoluteUrl(alternate);
        upsertLink("alternate", { hreflang: "fr", href: frHref });
        upsertLink("alternate", { hreflang: "en", href: enHref });
        upsertLink("alternate", { hreflang: "x-default", href: `${SITE_URL}/` });
      }
    }

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "HollowCorp" });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: language === "en" ? "en_GB" : "fr_FR",
    });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: finalTitle });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: finalDescription,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: imageUrl,
    });
    upsertMeta('meta[property="og:image:type"]', { property: "og:image:type", content: "image/jpeg" });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: finalDescription,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });

    if (!shouldNoindex) {
      upsertJsonLd("ld-webpage", {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: finalTitle,
        description: finalDescription,
        inLanguage: language === "en" ? "en-GB" : "fr-FR",
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#organization` },
      });

      const crumbs = [
        { name: "HollowCorp", path: language === "en" ? "/en/home" : "/" },
      ];
      if (pathname !== "/" && pathname !== "/en/home") {
        crumbs.push({ name: finalTitle.replace(" | HollowCorp", ""), path: canonicalPath });
      }
      upsertJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: absoluteUrl(item.path),
        })),
      });
    }
  }, [pathname, title, description, image, canonical, noindex]);

  return null;
}
